import { useState } from "react";
import type { StepRow, PlayRow } from "../../hooks/usePlay";
import { useAssignStep } from "../../hooks/usePlay";
import type { Persona } from "../../hooks/usePersona";
import { StepEditModal } from "../StepEditModal";
import { DecisionDetail } from "./DecisionDetail";
import { CommDetail } from "./CommDetail";
import { QueryDetail } from "./QueryDetail";
import { MicroTaskDetail } from "./MicroTaskDetail";
import { WaitDetail } from "./WaitDetail";
import { MilestoneDetail } from "./MilestoneDetail";
import { InstrumentationDetail } from "./InstrumentationDetail";
import { ReviewContextPanel } from "./ReviewContextPanel";

export interface StepDetailProps {
  step: StepRow;
  play: PlayRow;
  workspaceId: string;
  currentUserId: string;
  persona: Persona | null;
}

function OwnerHeader({ step, workspaceId, playId }: { step: StepRow; workspaceId: string; playId: string }) {
  const [editOpen, setEditOpen] = useState(false);
  const assign = useAssignStep(workspaceId, playId);

  const mode = step.isAutonomous
    ? "Autonomous · runs unattended"
    : "Requires claim";
  const subAgent = step.isAutonomous && step.agentName
    ? ` · ${step.agentName}`
    : "";

  return (
    <>
      <div
        className="step-owner-header"
        data-testid="step-owner-header"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 12px",
          marginBottom: 8,
          borderRadius: 6,
          background: "var(--bg-soft)",
          border: "1px solid var(--border)",
          fontSize: 12,
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          {step.isAutonomous ? (
            <span title="Autonomous" aria-label="autonomous" style={{ color: "var(--accent)" }}>⚡</span>
          ) : null}
          <strong style={{ fontWeight: 600 }}>{step.displayOwner}</strong>
        </span>
        <span style={{ color: "var(--text-3)" }}>·</span>
        <span style={{ color: "var(--text-3)" }}>
          Mode: {mode}{subAgent}
        </span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => setEditOpen(true)}
          style={{
            fontSize: 11,
            padding: "3px 8px",
            borderRadius: 4,
            border: "1px solid var(--border)",
            background: "var(--bg)",
            color: "var(--text-2)",
            cursor: "pointer",
          }}
        >
          Reassign
        </button>
      </div>

      {editOpen && (
        <StepEditModal
          workspaceId={workspaceId}
          stepTitle={step.title}
          currentOwner={step.displayOwner}
          currentHandId={step.handId}
          currentBindingId={step.agentBindingId}
          isAutonomous={step.isAutonomous}
          onSave={({ handId, bindingId, autonomous }) => {
            if (!autonomous) {
              assign.mutate({ stepId: step.id, handId: null }, { onSuccess: () => setEditOpen(false) });
            } else if (bindingId) {
              assign.mutate({ stepId: step.id, bindingId }, { onSuccess: () => setEditOpen(false) });
            } else {
              assign.mutate({ stepId: step.id, handId: handId ?? null }, { onSuccess: () => setEditOpen(false) });
            }
          }}
          onClose={() => setEditOpen(false)}
          saving={assign.isPending}
        />
      )}
    </>
  );
}

export function StepDetail(props: StepDetailProps) {
  const { step } = props;
  let body: React.ReactNode;
  switch (step.type) {
    case "decision":
      body = <DecisionDetail {...props} />;
      break;
    case "comm":
      body = <CommDetail {...props} />;
      break;
    case "query":
      body = <QueryDetail {...props} />;
      break;
    case "micro_task":
      body = <MicroTaskDetail {...props} />;
      break;
    case "wait":
      body = <WaitDetail {...props} />;
      break;
    case "milestone":
      body = <MilestoneDetail {...props} />;
      break;
    case "instrumentation":
      body = <InstrumentationDetail {...props} />;
      break;
    default:
      return (
        <div className="ho-detail" data-testid="step-detail-unknown">
          Unknown step type: {String(step.type)}
        </div>
      );
  }
  return (
    <div data-testid="step-detail-wrap">
      <OwnerHeader step={step} workspaceId={props.workspaceId} playId={props.play.id} />
      <ReviewContextPanel workspaceId={props.workspaceId} stepId={step.id} />
      {body}
    </div>
  );
}

export {
  DecisionDetail,
  CommDetail,
  QueryDetail,
  MicroTaskDetail,
  WaitDetail,
  MilestoneDetail,
  InstrumentationDetail,
};
